import {
  ToggleOption_CheckoutConfigOptionGroupFragment,
  ToggleOption_CheckoutConfigPotentialOptionFragment,
} from "src/fragments/circularFragments.generated";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { shouldUseSwatchSelector } from "src/pages/configurator/Configurator.utils";
import { InteriorStep_CheckoutOptionGroupFragment } from "src/pages/configurator/stepTemplates/Interior.step.generated";
import { useTestIds } from "~utils";
import { PackageOptionCard } from "./PackageOptionCard";
import { ProductAddOnCard } from "./ProductAddOnCard";

type FlexibleOptionCardProps = {
  ccfgOptionGroup: InteriorStep_CheckoutOptionGroupFragment;
  onCompare?: (ccog?: InteriorStep_CheckoutOptionGroupFragment) => void;
  asChild?: boolean;
  toggleOptionSideEffects?: (
    ccog: ToggleOption_CheckoutConfigOptionGroupFragment,
    cco: ToggleOption_CheckoutConfigPotentialOptionFragment,
  ) => void;
};

/**
 * Flexible option card that automatically determines whether to render as a
 * ProductAddOnCard or PackageOptionCard based on the option group type.
 *
 * Handles when to show cutoff for structural options
 */
export function FlexibleOptionCard(props: FlexibleOptionCardProps) {
  const { ccfgOptionGroup, onCompare, toggleOptionSideEffects, asChild = false } = props;

  const { selectedOptionIds, autoSaving, toggleOption, showPriceLoadingState, checkoutConfig } =
    useConfiguratorContext();
  // Find the selected option ID for this group
  const selectedOptionId = ccfgOptionGroup.options.find((option) => selectedOptionIds.includes(option.option.id))
    ?.option.id;

  const isDisabled = (checkoutConfig ? !checkoutConfig?.canEdit.allowed : false) || autoSaving;

  const commonProps = {
    ccfgOptionGroup,
    selectedOptionIds,
    asChild,
    autoSaving: showPriceLoadingState(selectedOptionId),
    allOptionGroups: checkoutConfig?.optionGroups ?? [],
    onSelect: (
      ccog: ToggleOption_CheckoutConfigOptionGroupFragment,
      cco: ToggleOption_CheckoutConfigPotentialOptionFragment,
    ) => {
      toggleOption(ccog, cco);
      toggleOptionSideEffects?.(ccog, cco);
    },
  };

  const tid = useTestIds(props);

  if (shouldUseSwatchSelector(ccfgOptionGroup)) {
    const isStructuralOptionGroup = !!ccfgOptionGroup.group.group.type.isStructuralV2;
    const isPastStructuralCutoff = !checkoutConfig?.canChangeProductOffering.allowed;
    // Structural options need to hide their cutoff label if they're selected and past the structural cutoff
    const hideCutoff = isStructuralOptionGroup && isPastStructuralCutoff && !!selectedOptionId;

    return (
      <ProductAddOnCard
        key={ccfgOptionGroup.group.id}
        {...commonProps}
        disabled={isDisabled || (isStructuralOptionGroup && isPastStructuralCutoff)}
        onCompare={onCompare}
        hideCutoff={hideCutoff}
        displayPackageOptionsAsSingleProduct={!!ccfgOptionGroup.group.displayPackageOptionsAsSingleProduct}
        {...tid["productAddOnCard"]}
      />
    );
  }

  return (
    <PackageOptionCard
      {...commonProps}
      disabled={isDisabled}
      onCompare={onCompare ?? (() => {})}
      {...tid["packageOptionCard"]}
    />
  );
}
